// Cloudinary integration enabled
import React, { useState, useEffect } from 'react';
import { Palette, Upload, Image, Globe, Settings, X, Bug, Eye, EyeOff, Copy, Check, Save, RefreshCw, ExternalLink } from 'lucide-react';
import { toast } from 'react-hot-toast';
import axios from '../../utils/axios';
import { useTranslation } from 'react-i18next';

interface MenuSettingsProps {
  menuId: string;
  onUpdate: () => void;
}

const FONT_FAMILIES = [
  // Sans-serif (Web Safe)
  { value: 'Arial', label: 'Arial', group: 'Sans-serif' },
  { value: 'Helvetica', label: 'Helvetica', group: 'Sans-serif' },
  { value: 'Verdana', label: 'Verdana', group: 'Sans-serif' },
  { value: 'Tahoma', label: 'Tahoma', group: 'Sans-serif' },
  { value: 'Trebuchet MS', label: 'Trebuchet MS', group: 'Sans-serif' },
  // Serif (Web Safe)
  { value: 'Times New Roman', label: 'Times New Roman', group: 'Serif' },
  { value: 'Georgia', label: 'Georgia', group: 'Serif' },
  { value: 'Garamond', label: 'Garamond', group: 'Serif' },
  { value: 'Palatino', label: 'Palatino', group: 'Serif' },
  // Monospace
  { value: 'Courier New', label: 'Courier New', group: 'Monospace' },
  // Google Fonts - Sans-serif
  { value: 'Roboto', label: 'Roboto', group: 'Google Fonts' },
  { value: 'Open Sans', label: 'Open Sans', group: 'Google Fonts' },
  { value: 'Lato', label: 'Lato', group: 'Google Fonts' },
  { value: 'Montserrat', label: 'Montserrat', group: 'Google Fonts' },
  { value: 'Inter', label: 'Inter', group: 'Google Fonts' },
  { value: 'Poppins', label: 'Poppins', group: 'Google Fonts' },
  { value: 'Raleway', label: 'Raleway', group: 'Google Fonts' },
  { value: 'Nunito', label: 'Nunito', group: 'Google Fonts' },
  { value: 'Source Sans Pro', label: 'Source Sans Pro', group: 'Google Fonts' },
  { value: 'Oswald', label: 'Oswald', group: 'Google Fonts' },
  { value: 'Quicksand', label: 'Quicksand', group: 'Google Fonts' },
  { value: 'Josefin Sans', label: 'Josefin Sans', group: 'Google Fonts' },
  { value: 'Work Sans', label: 'Work Sans', group: 'Google Fonts' },
  { value: 'DM Sans', label: 'DM Sans', group: 'Google Fonts' },
  { value: 'Rubik', label: 'Rubik', group: 'Google Fonts' },
  // Google Fonts - Serif / Display
  { value: 'Playfair Display', label: 'Playfair Display', group: 'Google Fonts - Elegant' },
  { value: 'Merriweather', label: 'Merriweather', group: 'Google Fonts - Elegant' },
  { value: 'Lora', label: 'Lora', group: 'Google Fonts - Elegant' },
  { value: 'Cormorant Garamond', label: 'Cormorant Garamond', group: 'Google Fonts - Elegant' },
  { value: 'Libre Baskerville', label: 'Libre Baskerville', group: 'Google Fonts - Elegant' },
  { value: 'EB Garamond', label: 'EB Garamond', group: 'Google Fonts - Elegant' },
  { value: 'Crimson Text', label: 'Crimson Text', group: 'Google Fonts - Elegant' },
  // Google Fonts - Handwritten / Decorative
  { value: 'Dancing Script', label: 'Dancing Script', group: 'Google Fonts - Decorative' },
  { value: 'Pacifico', label: 'Pacifico', group: 'Google Fonts - Decorative' },
  { value: 'Great Vibes', label: 'Great Vibes', group: 'Google Fonts - Decorative' },
  { value: 'Sacramento', label: 'Sacramento', group: 'Google Fonts - Decorative' },
  { value: 'Satisfy', label: 'Satisfy', group: 'Google Fonts - Decorative' },
];

// Group fonts by category for optgroup rendering
const FONT_GROUPS = FONT_FAMILIES.reduce((acc, font) => {
  if (!acc[font.group]) acc[font.group] = [];
  acc[font.group].push(font);
  return acc;
}, {} as Record<string, typeof FONT_FAMILIES>);

/** Default background images from public folder - path is relative to site root */
const DEFAULT_BACKGROUND_GALLERY = [
  { path: '/one.jpg', label: 'Sfondo 1' },
  { path: '/three.jpg', label: 'Sfondo 2' },
  { path: '/theme3.jpeg', label: 'Sfondo 3' },
  { path: '/theme4.jpeg', label: 'Sfondo 4' },
  { path: '/theme1.jpeg', label: 'Sfondo scuro 1' },
  { path: '/theme2.jpeg', label: 'Sfondo scuro 2' },
];

interface MenuStyleConfig {
  primaryColor: string;
  secondaryColor: string;
  backgroundColor: string;
  textColor: string;
  headerColor: string;
  fontFamily: string;
  fontSize: string;
  // Per-section typography
  fontFamilyHeader?: string;
  fontSizeHeader?: string;
  fontFamilyMacroSection?: string;
  fontSizeMacroSection?: string;
  fontFamilyCategory?: string;
  fontSizeCategory?: string;
  fontFamilyDish?: string;
  fontSizeDish?: string;
  fontFamilyDescription?: string;
  fontSizeDescription?: string;
  fontFamilyFooter?: string;
  fontSizeFooter?: string;
  fontFamilyPrice?: string;
  fontSizePrice?: string;
  fontWeightPrice?: string;
  priceColor?: string;
  borderRadius: string;
  boxShadow: string;
  logoUrl?: string;
  backgroundImage?: string | null;
  overlayColor?: string;
  overlayOpacity?: number;
  logoSize?: string;
  fontWeightDish?: string;
  cardBorderColor?: string;
  cardBorderWidth?: number;
  showItemBorder?: boolean;
}

interface StyleConfigResponse {
  theme?: string;
  publicMenuPage?: string;
  primary?: string;
  secondary?: string;
  background?: string;
  text?: string;
  header?: string;
  accent?: string;
  light?: string;
  dark?: string;
  fontFamily?: string;
  fontSize?: string;
  fontFamilyHeader?: string;
  fontSizeHeader?: string;
  fontFamilyMacroSection?: string;
  fontSizeMacroSection?: string;
  fontFamilyCategory?: string;
  fontSizeCategory?: string;
  fontFamilyDish?: string;
  fontSizeDish?: string;
  fontFamilyDescription?: string;
  fontSizeDescription?: string;
  fontFamilyFooter?: string;
  fontSizeFooter?: string;
  fontFamilyPrice?: string;
  fontSizePrice?: string;
  fontWeightPrice?: string;
  priceColor?: string;
  borderRadius?: string;
  boxShadow?: string;
  logoUrl?: string;
  backgroundImage?: string | null;
  primaryColor?: string;
  secondaryColor?: string;
  backgroundColor?: string;
  textColor?: string;
  headerColor?: string;
  overlayColor?: string;
  overlayOpacity?: number;
  logoSize?: string;
  fontWeightDish?: string;
  cardBorderColor?: string;
  cardBorderWidth?: number;
  showItemBorder?: boolean;
}

const MenuSettings: React.FC<MenuSettingsProps> = ({ menuId, onUpdate }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string>('');
  const [backgroundImageFile, setBackgroundImageFile] = useState<File | null>(null);
  const [backgroundImagePreview, setBackgroundImagePreview] = useState<string>('');
  const [uploadingBackground, setUploadingBackground] = useState(false);
  const [settings, setSettings] = useState<MenuStyleConfig>({
    primaryColor: '#7BD5B5',
    secondaryColor: '#0A0C0B',
    backgroundColor: '#ffffff',
    textColor: '#0A0C0B',
    headerColor: '#0A0C0B',
    fontFamily: 'Arial',
    fontSize: '14px',
    borderRadius: '8px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    logoUrl: '',
    logoSize: 'medium',
    fontWeightDish: '700',
    cardBorderColor: '#E5E7EB',
    cardBorderWidth: 1,
    showItemBorder: true
  });
  const [selectedTheme, setSelectedTheme] = useState<'light' | 'dark' | 'lightComplex' | 'darkComplex'>('light');
  const [publicMenuPage, setPublicMenuPage] = useState<'light' | 'dark' | 'default' | 'lightComplex' | 'darkComplex'>('default');
  const [showUrlSelector, setShowUrlSelector] = useState(false);
  const [diagnosing, setDiagnosing] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const [menuSlug, setMenuSlug] = useState<string>('');
  const [previewKey, setPreviewKey] = useState(0);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchMenuSettings();
    
    // Cleanup function for blob URLs
    return () => {
      if (logoPreview && logoPreview.startsWith('blob:')) {
        URL.revokeObjectURL(logoPreview);
      }
      if (backgroundImagePreview && backgroundImagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(backgroundImagePreview);
      }
    };
  }, [menuId]);

  useEffect(() => {
    if (showPreview) {
      setPreviewKey(prev => prev + 1);
    }
  }, [settings, showPreview]);

  useEffect(() => {
    return () => {
      if (logoPreview && logoPreview.startsWith('blob:')) {
        URL.revokeObjectURL(logoPreview);
      }
    };
  }, [logoPreview]);

  useEffect(() => {
    return () => {
      if (backgroundImagePreview && backgroundImagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(backgroundImagePreview);
      }
    };
  }, [backgroundImagePreview]);

  const fetchMenuSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/menu/${menuId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        const menuData = response.data.data;
        
        let slug = '';
        if (menuData.slug) {
          slug = menuData.slug;
        }
        
        if (slug) {
          setMenuSlug(slug);
        }
        
        let styleConfig: StyleConfigResponse = {};
        if (menuData.styleConfig) {
          try {
            let parsedString = typeof menuData.styleConfig === 'string' 
              ? JSON.parse(menuData.styleConfig) 
              : menuData.styleConfig;
            
            if (typeof parsedString === 'string') {
              styleConfig = JSON.parse(parsedString);
            } else {
              styleConfig = parsedString;
            }
          } catch (error) {
            console.error('Failed to parse styleConfig:', error);
          }
        }
        
        const savedTheme = styleConfig.theme || 'light';
        setSelectedTheme(savedTheme as 'light' | 'dark' | 'lightComplex' | 'darkComplex');
        
        const savedPublicMenuUrl = menuData.publicMenuUrl || 'default';
        setPublicMenuPage(savedPublicMenuUrl as 'light' | 'dark' | 'default' | 'lightComplex' | 'darkComplex' );
        
        const restaurantLogoUrl = menuData.restaurant?.logoUrl;
        const styleConfigLogoUrl = styleConfig?.logoUrl;
        let logoUrl = restaurantLogoUrl || styleConfigLogoUrl || '';
        
        let fullLogoUrl = logoUrl;
        if (logoUrl && logoUrl.startsWith('/') && !logoUrl.startsWith('http')) {
          const baseUrl = import.meta.env.VITE_API_URL?.replace('/api', '') || window.location.origin;
          fullLogoUrl = `${baseUrl}${logoUrl}`;
        }
        
        setSettings({
          primaryColor: styleConfig?.primary || '#7BD5B5',
          secondaryColor: styleConfig?.secondary || '#0A0C0B',
          backgroundColor: styleConfig?.background || '#ffffff',
          textColor: styleConfig?.text || '#0A0C0B',
          headerColor: styleConfig?.header || '#0A0C0B',
          fontFamily: styleConfig?.fontFamily || 'Arial',
          fontSize: styleConfig?.fontSize || '14px',
          fontFamilyHeader: styleConfig?.fontFamilyHeader ?? styleConfig?.fontFamily ?? 'Arial',
          fontSizeHeader: styleConfig?.fontSizeHeader ?? '18px',
          fontFamilyMacroSection: styleConfig?.fontFamilyMacroSection ?? styleConfig?.fontFamily ?? 'Arial',
          fontSizeMacroSection: styleConfig?.fontSizeMacroSection ?? '18px',
          fontFamilyCategory: styleConfig?.fontFamilyCategory ?? styleConfig?.fontFamily ?? 'Arial',
          fontSizeCategory: styleConfig?.fontSizeCategory ?? '14px',
          fontFamilyDish: styleConfig?.fontFamilyDish ?? styleConfig?.fontFamily ?? 'Arial',
          fontSizeDish: styleConfig?.fontSizeDish ?? '16px',
          fontFamilyDescription: styleConfig?.fontFamilyDescription ?? styleConfig?.fontFamily ?? 'Arial',
          fontSizeDescription: styleConfig?.fontSizeDescription ?? '14px',
          fontFamilyFooter: styleConfig?.fontFamilyFooter ?? styleConfig?.fontFamily ?? 'Arial',
          fontSizeFooter: styleConfig?.fontSizeFooter ?? '14px',
          fontFamilyPrice: styleConfig?.fontFamilyPrice ?? styleConfig?.fontFamilyDish ?? styleConfig?.fontFamily ?? 'Arial',
          fontSizePrice: styleConfig?.fontSizePrice ?? styleConfig?.fontSizeDish ?? '16px',
          fontWeightPrice: styleConfig?.fontWeightPrice ?? '700',
          priceColor: styleConfig?.priceColor ?? styleConfig?.secondary ?? '#0A0C0B',
          borderRadius: styleConfig?.borderRadius || '8px',
          boxShadow: styleConfig?.boxShadow || '0 4px 6px rgba(0, 0, 0, 0.1)',
          logoUrl: fullLogoUrl,
          backgroundImage: styleConfig?.backgroundImage || '',
          overlayColor: styleConfig?.overlayColor ?? '#000000',
          overlayOpacity: styleConfig?.overlayOpacity ?? 0,
          logoSize: styleConfig?.logoSize ?? 'medium',
          fontWeightDish: styleConfig?.fontWeightDish ?? '700',
          cardBorderColor: styleConfig?.cardBorderColor ?? '#E5E7EB',
          cardBorderWidth: styleConfig?.cardBorderWidth ?? 1,
          showItemBorder: styleConfig?.showItemBorder !== false
        });
        
        if (styleConfig?.backgroundImage) {
          let bgImageUrl = styleConfig.backgroundImage;
          if (bgImageUrl.startsWith('/') && !bgImageUrl.startsWith('http')) {
            const baseUrl = import.meta.env.VITE_API_URL?.replace('/api', '') || window.location.origin;
            bgImageUrl = `${baseUrl}${bgImageUrl}`;
          }
          setBackgroundImagePreview(bgImageUrl);
        }
        
        setLogoPreview(fullLogoUrl);
      }
    } catch (error) {
      console.error('Failed to fetch menu settings:', error);
      toast.error(t('menu_settings.messages.load_error'));
    } finally {
      setLoading(false);
    }
  };

  const handleColorChange = (field: keyof MenuStyleConfig, value: string) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const applyThemeColors = (theme: 'light' | 'dark') => {
    if (theme === 'light') {
      setSettings(prev => ({
        ...prev,
        primaryColor: '#7BD5B5',
        secondaryColor: '#0A0C0B',
        backgroundColor: '#ffffff',
        textColor: '#0A0C0B',
        headerColor: '#0A0C0B'
      }));
    } else {
      setSettings(prev => ({
        ...prev,
        primaryColor: '#7BD5B5',
        secondaryColor: '#ffffff',
        backgroundColor: '#1a1a1a',
        textColor: '#ffffff',
        headerColor: '#ffffff'
      }));
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error(t('menu_settings.messages.invalid_image'));
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('menu_settings.messages.image_size_limit'));
      return;
    }

    if (logoPreview && logoPreview.startsWith('blob:')) {
      URL.revokeObjectURL(logoPreview);
    }
    
    const previewUrl = URL.createObjectURL(file);
    setLogoPreview(previewUrl);
    setLogoFile(file);

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('logo', file);
      
      const response = await axios.post(`/menu/${menuId}/upload-logo-cloudinary`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        const cloudinaryUrl = response.data.data.logoUrl;
        
        setSettings(prev => ({ 
          ...prev, 
          logoUrl: cloudinaryUrl 
        }));
        
        await handleSaveSettings();
        
        toast.success(t('menu_settings.logo.upload_success'));
        onUpdate();
      }
    } catch (error: any) {
      console.error('Logo upload error:', error);
      
      URL.revokeObjectURL(previewUrl);
      setLogoPreview('');
      setLogoFile(null);
      
      toast.error(error.response?.data?.message || t('menu_settings.logo.upload_error'));
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveLogo = async () => {
    if (!settings.logoUrl) {
      if (logoPreview && logoPreview.startsWith('blob:')) {
        URL.revokeObjectURL(logoPreview);
      }
      setLogoFile(null);
      setLogoPreview('');
      setSettings(prev => ({ ...prev, logoUrl: '' }));
      return;
    }

    if (!window.confirm(t('menu_settings.logo.remove_confirm'))) {
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      const response = await axios.delete(`/menu/${menuId}/remove-logo`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        if (logoPreview && logoPreview.startsWith('blob:')) {
          URL.revokeObjectURL(logoPreview);
        }
        
        setLogoFile(null);
        setLogoPreview('');
        setSettings(prev => ({ ...prev, logoUrl: '' }));
        
        await handleSaveSettings();
        
        toast.success(t('menu_settings.logo.removed_success'));
        onUpdate();
      }
    } catch (error: any) {
      console.error('Failed to remove logo:', error);
      toast.error(error.response?.data?.message || t('menu_settings.logo.removed_error'));
    } finally {
      setSaving(false);
    }
  };

  const handleBackgroundImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error(t('menu_settings.messages.invalid_image'));
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        toast.error(t('menu_settings.messages.image_size_limit'));
        return;
      }
      
      const previewUrl = URL.createObjectURL(file);
      setBackgroundImagePreview(previewUrl);
      setBackgroundImageFile(file);
      
      setUploadingBackground(true);
      try {
        const token = localStorage.getItem('token');
        const formData = new FormData();
        formData.append('image', file);
        
        const response = await axios.post(`/menu/${menuId}/upload-background-image`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        
        if (response.data.success) {
          const cloudinaryUrl = response.data.data.imageUrl;
          
          await handleSaveSettingsWithBackgroundImage(cloudinaryUrl);
          
          toast.success(t('menu_settings.background.upload_success'));
        }
      } catch (error: any) {
        console.error('Background image upload error:', error);
        toast.error(error.response?.data?.error || t('menu_settings.background.upload_error'));
        const reader = new FileReader();
        reader.onloadend = () => {
          setBackgroundImagePreview(reader.result as string);
          toast.success(t('menu_settings.background.ready_for_preview'));
        };
        reader.readAsDataURL(file);
      } finally {
        setUploadingBackground(false);
      }
    }
  };

  const handleSelectGalleryBackground = async (path: string) => {
    const baseUrl = window.location.origin;
    const fullUrl = path.startsWith('http') ? path : `${baseUrl}${path}`;
    setBackgroundImagePreview(fullUrl);
    setBackgroundImageFile(null);
    setSettings(prev => ({ ...prev, backgroundImage: fullUrl }));
    try {
      await handleSaveSettingsWithBackgroundImage(fullUrl);
      toast.success(t('menu_settings.background.gallery_selected'));
      onUpdate();
    } catch (err) {
      toast.error(t('menu_settings.background.gallery_error'));
    }
  };

  const handleSaveSettingsWithBackgroundImage = async (backgroundImageUrl: string) => {
    try {
      const token = localStorage.getItem('token');
      
      const currentResponse = await axios.get(`/menu/${menuId}/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      let currentStyleConfig: any = {};
      if (currentResponse.data.data?.styleConfig) {
        try {
          const parsed = typeof currentResponse.data.data.styleConfig === 'string' 
            ? JSON.parse(currentResponse.data.data.styleConfig) 
            : currentResponse.data.data.styleConfig;
          
          currentStyleConfig = typeof parsed === 'string' ? JSON.parse(parsed) : parsed;
        } catch (error) {
          console.error('Error parsing current styleConfig:', error);
        }
      }
      
      const updatedStyleConfig = {
        ...currentStyleConfig,
        theme: 'light',
        primary: settings.primaryColor,
        secondary: settings.secondaryColor,
        background: settings.backgroundColor,
        text: settings.textColor,
        header: settings.headerColor,
        fontFamily: settings.fontFamily,
        fontSize: settings.fontSize,
        fontFamilyHeader: settings.fontFamilyHeader ?? settings.fontFamily,
        fontSizeHeader: settings.fontSizeHeader ?? '18px',
        fontFamilyMacroSection: settings.fontFamilyMacroSection ?? settings.fontFamily,
        fontSizeMacroSection: settings.fontSizeMacroSection ?? '18px',
        fontFamilyCategory: settings.fontFamilyCategory ?? settings.fontFamily,
        fontSizeCategory: settings.fontSizeCategory ?? '14px',
        fontFamilyDish: settings.fontFamilyDish ?? settings.fontFamily,
        fontSizeDish: settings.fontSizeDish ?? '16px',
        fontFamilyDescription: settings.fontFamilyDescription ?? settings.fontFamily,
        fontSizeDescription: settings.fontSizeDescription ?? '14px',
        fontFamilyFooter: settings.fontFamilyFooter ?? settings.fontFamily,
        fontSizeFooter: settings.fontSizeFooter ?? '14px',
        fontFamilyPrice: settings.fontFamilyPrice ?? settings.fontFamilyDish ?? settings.fontFamily,
        fontSizePrice: settings.fontSizePrice ?? settings.fontSizeDish ?? '16px',
        fontWeightPrice: settings.fontWeightPrice ?? '700',
        priceColor: settings.priceColor ?? settings.secondaryColor,
        borderRadius: settings.borderRadius,
        boxShadow: settings.boxShadow,
        logoUrl: settings.logoUrl === 'NEW_UPLOAD' ? currentStyleConfig.logoUrl : settings.logoUrl,
        backgroundImage: backgroundImageUrl,
        logoSize: settings.logoSize ?? 'medium',
        fontWeightDish: settings.fontWeightDish ?? '700'
      };
      
      await axios.put(`/menu/${menuId}/settings`, {
        styleConfig: JSON.stringify(updatedStyleConfig)
      }, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      setSettings(prev => ({ ...prev, backgroundImage: backgroundImageUrl }));
      
    } catch (error: any) {
      console.error('Failed to save settings with background image:', error);
      throw error;
    }
  };

  const handleRemoveBackgroundImage = async () => {
    if (backgroundImagePreview && backgroundImagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(backgroundImagePreview);
    }
    
    setBackgroundImageFile(null);
    setBackgroundImagePreview('');
    
    // Update settings with null backgroundImage to remove it from DB
    setSettings(prev => ({ ...prev, backgroundImage: null }));
    
    // Save to database immediately
    try {
      const token = localStorage.getItem('token');
      
      // Get current styleConfig first
      let currentStyleConfig: any = {};
      try {
        const currentResponse = await axios.get(`/menu/${menuId}/settings`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (currentResponse.data.data?.styleConfig) {
          const parsed = typeof currentResponse.data.data.styleConfig === 'string' 
            ? JSON.parse(currentResponse.data.data.styleConfig) 
            : currentResponse.data.data.styleConfig;
          
          currentStyleConfig = typeof parsed === 'string' ? JSON.parse(parsed) : parsed;
        }
      } catch (error) {
        console.error('Error fetching current styleConfig:', error);
      }
      
      // Update styleConfig with null backgroundImage
      const updatedStyleConfig = {
        ...currentStyleConfig,
        backgroundImage: null
      };
      
      await axios.put(`/menu/${menuId}/settings`, {
        styleConfig: JSON.stringify(updatedStyleConfig)
      }, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      toast.success(t('menu_settings.background.removed')); // Show success message
      onUpdate(); // Trigger parent update
      
    } catch (error: any) {
      console.error('Failed to remove background image:', error);
      toast.error(t('menu_settings.background.remove_failed'));
    }
  };

  const handleDiagnoseBackgroundImage = async () => {
    setDiagnosing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/menu/${menuId}/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      let styleConfig: any = {};
      if (response.data.data?.styleConfig) {
        try {
          if (typeof response.data.data.styleConfig === 'string') {
            const parsed = JSON.parse(response.data.data.styleConfig);
            styleConfig = typeof parsed === 'string' ? JSON.parse(parsed) : parsed;
          } else {
            styleConfig = response.data.data.styleConfig;
          }
        } catch (error) {
          console.error('StyleConfig parse error:', error);
          toast.error(t('menu_settings.messages.parse_error'));
          return;
        }
      }

      const backgroundImage = styleConfig.backgroundImage as string | undefined;
      const hasBackgroundImage = !!backgroundImage;
      
      const isCloudinaryUrl = backgroundImage?.includes('res.cloudinary.com');
      
      let imageAccessible = false;
      if (backgroundImage) {
        try {
          const imageResponse = await fetch(backgroundImage, { method: 'HEAD' });
          imageAccessible = imageResponse.ok;
        } catch (error) {
          console.error('Image access error:', error);
        }
      }

      toast.success(t('menu_settings.messages.diagnosis_success', { parsed: !!response.data.data?.styleConfig }));
      
      if (hasBackgroundImage) {
        toast.success(t('menu_settings.messages.background_found', { url: backgroundImage?.substring(0, 50) }));
      } else {
        toast(t('menu_settings.messages.no_background'));
      }

      if (isCloudinaryUrl) {
        toast.success(t('menu_settings.messages.cloudinary_detected'));
      } else if (hasBackgroundImage) {
        toast(t('menu_settings.messages.not_cloudinary'));
      }

      if (imageAccessible) {
        toast.success(t('menu_settings.messages.image_accessible'));
      } else if (hasBackgroundImage) {
        toast(t('menu_settings.messages.image_not_accessible'));
      }

    } catch (error: any) {
      console.error('Diagnosis failed:', error);
      toast.error(t('menu_settings.messages.diagnosis_failed', { error: error.message }));
    } finally {
      setDiagnosing(false);
    }
  };

  const handlePublicUrlSelect = async (page: 'light' | 'dark' | 'default' | 'lightComplex' | 'darkComplex') => {
    setPublicMenuPage(page);
    setPreviewKey(prev => prev + 1);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      const response = await axios.put(
        `/menu/${menuId}/public-url`, 
        { publicMenuUrl: page },
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      toast.success(t('menu_settings.public_url.selected', { theme: page.charAt(0).toUpperCase() + page.slice(1) }));
      
    } catch (error: any) {
      console.error('❌ API FAILED:', error);
      toast.error('Failed to update public menu URL');
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      let currentStyleConfig: any = {};
      try {
        const currentResponse = await axios.get(`/menu/${menuId}/settings`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (currentResponse.data.data?.styleConfig) {
          const parsed = typeof currentResponse.data.data.styleConfig === 'string' 
            ? JSON.parse(currentResponse.data.data.styleConfig) 
            : currentResponse.data.data.styleConfig;
          
          currentStyleConfig = typeof parsed === 'string' ? JSON.parse(parsed) : parsed;
        }
      } catch (error) {
        console.error('Error fetching current styleConfig:', error);
      }
      
      const styleConfigToSave = {
        ...currentStyleConfig,
        theme: selectedTheme,
        primary: settings.primaryColor,
        secondary: settings.secondaryColor,
        background: settings.backgroundColor,
        text: settings.textColor,
        header: settings.headerColor,
        fontFamily: settings.fontFamily,
        fontSize: settings.fontSize,
        fontFamilyHeader: settings.fontFamilyHeader ?? settings.fontFamily,
        fontSizeHeader: settings.fontSizeHeader ?? '18px',
        fontFamilyMacroSection: settings.fontFamilyMacroSection ?? settings.fontFamily,
        fontSizeMacroSection: settings.fontSizeMacroSection ?? '18px',
        fontFamilyCategory: settings.fontFamilyCategory ?? settings.fontFamily,
        fontSizeCategory: settings.fontSizeCategory ?? '14px',
        fontFamilyDish: settings.fontFamilyDish ?? settings.fontFamily,
        fontSizeDish: settings.fontSizeDish ?? '16px',
        fontFamilyDescription: settings.fontFamilyDescription ?? settings.fontFamily,
        fontSizeDescription: settings.fontSizeDescription ?? '14px',
        fontFamilyFooter: settings.fontFamilyFooter ?? settings.fontFamily,
        fontSizeFooter: settings.fontSizeFooter ?? '14px',
        fontFamilyPrice: settings.fontFamilyPrice ?? settings.fontFamilyDish ?? settings.fontFamily,
        fontSizePrice: settings.fontSizePrice ?? settings.fontSizeDish ?? '16px',
        fontWeightPrice: settings.fontWeightPrice ?? '700',
        priceColor: settings.priceColor ?? settings.secondaryColor,
        borderRadius: settings.borderRadius,
        boxShadow: settings.boxShadow,
        logoUrl: settings.logoUrl === 'NEW_UPLOAD' ? currentStyleConfig.logoUrl : settings.logoUrl,
        backgroundImage: settings.backgroundImage || '',
        overlayColor: settings.overlayColor ?? '#000000',
        overlayOpacity: settings.overlayOpacity ?? 0,
        logoSize: settings.logoSize ?? 'medium',
        fontWeightDish: settings.fontWeightDish ?? '700',
        cardBorderColor: settings.cardBorderColor ?? '#E5E7EB',
        cardBorderWidth: settings.cardBorderWidth ?? 1,
        showItemBorder: settings.showItemBorder !== false
      };
      
      await axios.put(`/menu/${menuId}/settings`, {
        styleConfig: JSON.stringify(styleConfigToSave)
      }, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      toast.success(t('menu_settings.messages.save_success'));
      onUpdate();
    } catch (error: any) {
      console.error('Failed to save settings:', error);
      toast.error(error.response?.data?.message || t('menu_settings.messages.save_error'));
    } finally {
      setSaving(false);
    }
  };

  const getPreviewUrl = () => {
    if (menuSlug) {
      const baseUrl = window.location.origin;
      let route;
      
      if (publicMenuPage === 'dark') {
        route = '/menu-dark/';
      } else if (publicMenuPage === 'light') {
        route = '/menu-light/';
      } else if (publicMenuPage === 'lightComplex') {
        route = '/menu-lightComplex/';
      } else if (publicMenuPage === 'darkComplex') {
        route = '/menu-darkComplex/';
      } else {
        route = '/menu/';
      }
      
      return `${baseUrl}${route}${menuSlug}?preview=true&settings=${Date.now()}`;
    } else {
      let route;
      if (publicMenuPage === 'dark') {
        route = '/menu-dark/preview/';
      } else if (publicMenuPage === 'light') {
        route = '/menu-light/preview/';
      } else if (publicMenuPage === 'lightComplex') {
        route = '/menu-lightComplex/preview/';
      }  else if (publicMenuPage === 'darkComplex') {
        route = '/menu-darkComplex/preview/';
      }
      else {
        route = '/menu/preview/';
      }
      return `${window.location.origin}${route}${menuId}?preview=true&settings=${Date.now()}`;
    }
  };

  const handleOpenInNewTab = () => {
    window.open(getPreviewUrl(), '_blank');
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#7BD5B5]"></div>
        <span className="ml-3 text-gray-600">{t('menu_settings.loading')}</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                <div className="p-2 bg-[#7BD5B5]/10 rounded-lg">
                  <Settings className="w-6 h-6 text-[#7BD5B5]" />
                </div>
                {t('menu_settings.title')}
              </h1>
              <p className="text-gray-600 mt-1">{t('menu_settings.subtitle')}</p>
            </div>
            
            {/* Save Button */}
            <button
              type="button"
              onClick={handleSaveSettings}
              disabled={saving}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#0A0C0B] hover:bg-gray-900 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  {t('menu_settings.save_button.saving')}
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {t('menu_settings.save_button.save_all')}
                </>
              )}
            </button>
          </div>
        </div>

      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column - Media Uploads */}
        <div className="space-y-6">
          {/* Logo Upload Card */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <div className="p-1.5 bg-blue-50 rounded">
                  <Image className="w-4 h-4 text-blue-600" />
                </div>
                {t('menu_settings.logo.title')}
              </h3>
            </div>
            
            <div className="space-y-4">
              <div className="flex flex-col items-center">
                {logoPreview ? (
                  <div className="relative mb-4">
                    <div className="w-40 h-40 rounded-lg border-2 border-gray-200 overflow-hidden bg-gray-50">
                      <img 
                        src={logoPreview} 
                        alt="Logo preview" 
                        className="w-full h-full object-contain p-4"
                        onError={(e) => {
                          const target = e.currentTarget as HTMLImageElement;
                          target.style.display = 'none';
                          const parent = target.parentElement;
                          if (parent) {
                            parent.innerHTML = 
                              `<div class="w-full h-full flex flex-col items-center justify-center text-gray-400 bg-gray-100">
                                <Image class="w-8 h-8 mb-2" />
                                <div class="text-sm">Failed to load</div>
                              </div>`;
                          }
                        }}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={handleRemoveLogo}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-7 h-7 flex items-center justify-center hover:bg-red-600 transition-colors shadow-sm"
                      title="Remove logo"
                      disabled={saving}
                    >
                      {saving ? (
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                      ) : (
                        <X className="w-3.5 h-3.5" />
                      )}
                    </button>
                  </div>
                ) : (
                  <div className="w-40 h-40 rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center bg-gray-50 mb-4">
                    <div className="text-center p-4">
                      <Image className="w-8 h-8 text-gray-400 mx-auto mb-3" />
                      <p className="text-sm text-gray-500">{t('menu_settings.logo.no_logo')}</p>
                    </div>
                  </div>
                )}
                
                <div className="flex gap-2 w-full max-w-xs">
                  <label className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-[#0A0C0B] hover:bg-gray-900 text-white font-medium rounded-lg cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                    <Upload className="w-4 h-4" />
                    {logoPreview ? t('menu_settings.logo.change') : t('menu_settings.logo.upload')}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleFileChange}
                      disabled={saving}
                    />
                  </label>
                </div>
              </div>
              
              <div className="text-center">
                <p className="text-xs text-gray-500">
                  {t('menu_settings.logo.recommendation')}
                </p>
              </div>
              
              <div className="pt-4 border-t border-gray-100">
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('menu_settings.logo.size_label', 'Logo size')}</label>
                <select
                  value={settings.logoSize ?? 'medium'}
                  onChange={(e) => handleColorChange('logoSize', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#7BD5B5] focus:border-transparent bg-white"
                >
                  <option value="small">{t('menu_settings.logo.size_small', 'Small')}</option>
                  <option value="medium">{t('menu_settings.logo.size_medium', 'Medium')}</option>
                  <option value="large">{t('menu_settings.logo.size_large', 'Large')}</option>
                </select>
              </div>
            </div>
          </div>


          {/* Background Image Card */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <div className="p-1.5 bg-purple-50 rounded">
                  <Image className="w-4 h-4 text-purple-600" />
                </div>
                {t('menu_settings.background.title')}
              </h3>
              <button
                type="button"
                onClick={handleDiagnoseBackgroundImage}
                disabled={diagnosing}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 rounded-md transition-colors disabled:opacity-50"
                title="Diagnose background image issues"
              >
                <Bug className="w-3 h-3" />
                {diagnosing ? t('menu_settings.background.checking') : t('menu_settings.background.diagnose')}
              </button>
            </div>
            
            <div className="space-y-4">
               
              <div className="flex flex-col items-center">
                {backgroundImagePreview ? (
                  <div className="relative w-full mb-4">
                    <div className="rounded-lg border-2 border-gray-200 overflow-hidden bg-gray-50 max-h-48">
                      <img 
                        src={backgroundImagePreview} 
                        alt="Background preview" 
                        className="w-full h-48 object-cover"
                        onError={(e) => {
                          const target = e.currentTarget as HTMLImageElement;
                          target.style.display = 'none';
                          const parent = target.parentElement;
                          if (parent) {
                            parent.innerHTML = 
                              `<div class="w-full h-48 flex flex-col items-center justify-center text-gray-400 bg-gray-100">
                                <Image class="w-8 h-8 mb-2" />
                                <div class="text-sm">Failed to load</div>
                              </div>`;
                          }
                        }}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={handleRemoveBackgroundImage}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-7 h-7 flex items-center justify-center hover:bg-red-600 transition-colors shadow-sm"
                      title="Remove background image"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ) : (
                  <div className="w-full rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center bg-gray-50 h-48 mb-4">
                    <div className="text-center p-4">
                      <Image className="w-8 h-8 text-gray-400 mx-auto mb-3" />
                      <p className="text-sm text-gray-500">{t('menu_settings.background.no_image')}</p>
                    </div>
                  </div>
                )}
                
                <div className="flex gap-2 w-full">
                  <label className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 font-medium rounded-lg cursor-pointer transition-colors">
                    <Upload className="w-4 h-4" />
                    {backgroundImagePreview ? t('menu_settings.background.change') : t('menu_settings.background.upload')}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleBackgroundImageChange}
                      disabled={uploadingBackground}
                    />
                  </label>
                </div>

                <div className="pt-4 border-t border-gray-100">
                  <p className="text-sm font-medium text-gray-700 mb-3">{t('menu_settings.background.choose_from_gallery', 'Scegli dalla gallery')}</p>
                  <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                    {DEFAULT_BACKGROUND_GALLERY.map((item) => {
                      const fullUrl = item.path.startsWith('http') ? item.path : `${window.location.origin}${item.path}`;
                      const isSelected = backgroundImagePreview === fullUrl || (settings.backgroundImage === fullUrl && !backgroundImagePreview?.startsWith('blob:'));
                      return (
                        <button
                          key={item.path}
                          type="button"
                          onClick={() => handleSelectGalleryBackground(item.path)}
                          className={`relative aspect-video rounded-lg overflow-hidden border-2 transition-all focus:outline-none focus:ring-2 focus:ring-[#7BD5B5] focus:ring-offset-1 ${
                            isSelected ? 'border-[#7BD5B5] ring-2 ring-[#7BD5B5] ring-offset-1' : 'border-gray-200 hover:border-gray-300'
                          }`}
                          title={item.label}
                        >
                          <img
                            src={fullUrl}
                            alt={item.label}
                            className="w-full h-full object-cover"
                            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                          />
                          {isSelected && (
                            <span className="absolute inset-0 flex items-center justify-center bg-black/30">
                              <Check className="w-6 h-6 text-white" />
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Background Overlay */}
              <div className="pt-4 mt-4 border-t border-gray-200">
                <h4 className="text-sm font-medium text-gray-700 mb-3">{t('menu_settings.background.overlay_title', 'Sovrapposizione sfondo')}</h4>
                <p className="text-xs text-gray-500 mb-3">{t('menu_settings.background.overlay_desc', 'Strato semi-trasparente sopra lo sfondo per migliorare la leggibilità.')}</p>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('menu_settings.background.overlay_color', 'Colore overlay')}</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={settings.overlayColor ?? '#000000'}
                        onChange={(e) => setSettings(prev => ({ ...prev, overlayColor: e.target.value }))}
                        className="w-10 h-10 border border-gray-300 rounded-md cursor-pointer shadow-sm"
                      />
                      <input
                        type="text"
                        value={settings.overlayColor ?? '#000000'}
                        onChange={(e) => setSettings(prev => ({ ...prev, overlayColor: e.target.value }))}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#7BD5B5] focus:border-transparent"
                        placeholder="#000000"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      {t('menu_settings.background.overlay_opacity', 'Opacità overlay')}: {settings.overlayOpacity ?? 0}%
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={settings.overlayOpacity ?? 0}
                      onChange={(e) => setSettings(prev => ({ ...prev, overlayOpacity: parseInt(e.target.value, 10) }))}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#0A0C0B]"
                    />
                  </div>
                </div>
              </div>
              
              <div className="text-center">
                <p className="text-xs text-gray-500">
                  {t('menu_settings.background.recommendation')}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Styling Settings */}
        <div className="space-y-6">
          {/* Color Settings Card */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <div className="flex items-center gap-2 mb-4">
              <div className="p-1.5 bg-pink-50 rounded">
                <Palette className="w-4 h-4 text-pink-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">{t('menu_settings.colors.title')}</h3>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { label: t('menu_settings.colors.primary'), field: 'primaryColor' as const, value: settings.primaryColor },
                { label: t('menu_settings.colors.secondary'), field: 'secondaryColor' as const, value: settings.secondaryColor },
                { label: t('menu_settings.colors.background'), field: 'backgroundColor' as const, value: settings.backgroundColor },
                { label: t('menu_settings.colors.text'), field: 'textColor' as const, value: settings.textColor },
                { label: t('menu_settings.colors.header'), field: 'headerColor' as const, value: settings.headerColor }
              ].map(({ label, field, value }) => (
                <div key={field} className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    {label}
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={value}
                      onChange={(e) => handleColorChange(field, e.target.value)}
                      className="w-10 h-10 border border-gray-300 rounded-md cursor-pointer shadow-sm"
                    />
                    <input
                      type="text"
                      value={value}
                      onChange={(e) => handleColorChange(field, e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#7BD5B5] focus:border-transparent"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Card border (item box) */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <div className="flex items-center gap-2 mb-4">
              <div className="p-1.5 bg-amber-50 rounded">
                <Palette className="w-4 h-4 text-amber-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">{t('menu_settings.border.title', 'Bordo schede piatti')}</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">{t('menu_settings.border.subtitle', 'Colore e spessore del bordo intorno a ogni piatto nel menu pubblico.')}</p>
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200 mb-4">
              <label className="block text-sm font-medium text-gray-700">{t('menu_settings.border.show_border', 'Mostra bordo')}</label>
              <button
                type="button"
                role="switch"
                aria-checked={settings.showItemBorder !== false}
                onClick={() => setSettings(prev => ({ ...prev, showItemBorder: prev.showItemBorder === false }))}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-[#7BD5B5] focus:ring-offset-2 ${settings.showItemBorder !== false ? 'bg-[#0A0C0B]' : 'bg-gray-200'}`}
              >
                <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition ${settings.showItemBorder !== false ? 'translate-x-5' : 'translate-x-1'}`} style={{ top: '2px' }} />
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">{t('menu_settings.border.color', 'Colore bordo')}</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={settings.cardBorderColor ?? '#E5E7EB'}
                    onChange={(e) => handleColorChange('cardBorderColor', e.target.value)}
                    className="w-10 h-10 border border-gray-300 rounded-md cursor-pointer shadow-sm"
                  />
                  <input
                    type="text"
                    value={settings.cardBorderColor ?? '#E5E7EB'}
                    onChange={(e) => handleColorChange('cardBorderColor', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#7BD5B5] focus:border-transparent"
                    placeholder="#E5E7EB"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">{t('menu_settings.border.width', 'Spessore (px)')}</label>
                <input
                  type="number"
                  min={0}
                  max={20}
                  value={settings.cardBorderWidth ?? 1}
                  onChange={(e) => setSettings(prev => ({ ...prev, cardBorderWidth: Math.max(0, Math.min(20, parseInt(e.target.value, 10) || 0)) }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#7BD5B5] focus:border-transparent"
                  placeholder="1"
                />
              </div>
            </div>
          </div>

          {/* Typography Settings Card - Per Section */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('menu_settings.typography.title')}</h3>
            
            {[
              { key: 'header', label: t('menu_settings.typography.header', 'Header'), fontKey: 'fontFamilyHeader' as const, sizeKey: 'fontSizeHeader' as const },
              { key: 'macro', label: t('menu_settings.typography.macro_section', 'Macro Section'), fontKey: 'fontFamilyMacroSection' as const, sizeKey: 'fontSizeMacroSection' as const },
              { key: 'category', label: t('menu_settings.typography.category', 'Category'), fontKey: 'fontFamilyCategory' as const, sizeKey: 'fontSizeCategory' as const },
              { key: 'dish', label: t('menu_settings.typography.dish', 'Dish Items'), fontKey: 'fontFamilyDish' as const, sizeKey: 'fontSizeDish' as const },
              { key: 'description', label: t('menu_settings.typography.description', 'Description'), fontKey: 'fontFamilyDescription' as const, sizeKey: 'fontSizeDescription' as const },
              { key: 'footer', label: t('menu_settings.typography.footer', 'Footer'), fontKey: 'fontFamilyFooter' as const, sizeKey: 'fontSizeFooter' as const }
            ].map(({ key, label, fontKey, sizeKey }) => (
              <div key={key} className="mb-5 last:mb-0 pb-5 last:pb-0 border-b last:border-b-0 border-gray-100">
                <h4 className="text-sm font-semibold text-gray-800 mb-3">{label}</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">{t('menu_settings.typography.font_family')}</label>
                    <select
                      value={settings[fontKey] ?? settings.fontFamily}
                      onChange={(e) => handleColorChange(fontKey, e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#7BD5B5] focus:border-transparent bg-white"
                      style={{ fontFamily: settings[fontKey] ?? settings.fontFamily }}
                    >
                      {Object.entries(FONT_GROUPS).map(([group, fonts]) => (
                        <optgroup key={group} label={group}>
                          {fonts.map(f => (
                            <option key={f.value} value={f.value} style={{ fontFamily: f.value }}>
                              {f.label}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">{t('menu_settings.typography.font_size')}</label>
                    <input
                      type="text"
                      value={settings[sizeKey] ?? settings.fontSize ?? '14px'}
                      onChange={(e) => handleColorChange(sizeKey, e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#7BD5B5] focus:border-transparent bg-white"
                      placeholder={t('menu_settings.typography.font_size_placeholder', 'e.g. 14px, 1rem')}
                    />
                  </div>
                </div>
              </div>
            ))}
            
            <div className="mt-5 pt-5 border-t border-gray-200 space-y-3">
              <h4 className="text-sm font-semibold text-gray-800">{t('menu_settings.typography.other', 'Other')}</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('menu_settings.typography.dish_font_weight', 'Dish name font weight')}</label>
                  <select
                    value={settings.fontWeightDish ?? '700'}
                    onChange={(e) => handleColorChange('fontWeightDish', e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#7BD5B5] focus:border-transparent bg-white"
                  >
                    <option value="300">{t('menu_settings.typography.font_weight_light', 'Light')}</option>
                    <option value="400">{t('menu_settings.typography.font_weight_normal', 'Normal')}</option>
                    <option value="500">{t('menu_settings.typography.font_weight_medium', 'Medium')}</option>
                    <option value="600">{t('menu_settings.typography.font_weight_semibold', 'Semi-bold')}</option>
                    <option value="700">{t('menu_settings.typography.font_weight_bold', 'Bold')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('menu_settings.typography.border_radius')}</label>
                  <input
                    type="text"
                    value={settings.borderRadius}
                    onChange={(e) => handleColorChange('borderRadius', e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#7BD5B5] focus:border-transparent"
                    placeholder={t('menu_settings.typography.border_radius_placeholder')}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('menu_settings.typography.box_shadow')}</label>
                  <input
                    type="text"
                    value={settings.boxShadow}
                    onChange={(e) => handleColorChange('boxShadow', e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#7BD5B5] focus:border-transparent"
                    placeholder={t('menu_settings.typography.box_shadow_placeholder')}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Pricing Style Card */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('menu_settings.pricing_style.title', 'Pricing style')}</h3>
            <p className="text-sm text-gray-600 mb-4">{t('menu_settings.pricing_style.subtitle', 'Font, size and color for prices on the public menu.')}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t('menu_settings.pricing_style.font_family', 'Price font')}</label>
                <select
                  value={settings.fontFamilyPrice ?? settings.fontFamilyDish ?? settings.fontFamily}
                  onChange={(e) => handleColorChange('fontFamilyPrice', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#7BD5B5] focus:border-transparent bg-white"
                  style={{ fontFamily: settings.fontFamilyPrice ?? settings.fontFamilyDish ?? settings.fontFamily }}
                >
                  {Object.entries(FONT_GROUPS).map(([group, fonts]) => (
                    <optgroup key={group} label={group}>
                      {fonts.map(f => (
                        <option key={f.value} value={f.value} style={{ fontFamily: f.value }}>
                          {f.label}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t('menu_settings.pricing_style.font_size', 'Price font size')}</label>
                <input
                  type="text"
                  value={settings.fontSizePrice ?? settings.fontSizeDish ?? '16px'}
                  onChange={(e) => handleColorChange('fontSizePrice', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#7BD5B5] focus:border-transparent bg-white"
                  placeholder={t('menu_settings.typography.font_size_placeholder', 'e.g. 14px, 1rem')}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t('menu_settings.pricing_style.font_weight', 'Price font weight')}</label>
                <select
                  value={settings.fontWeightPrice ?? '700'}
                  onChange={(e) => handleColorChange('fontWeightPrice', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#7BD5B5] focus:border-transparent bg-white"
                >
                  <option value="300">{t('menu_settings.typography.font_weight_light', 'Light')}</option>
                  <option value="400">{t('menu_settings.typography.font_weight_normal', 'Normal')}</option>
                  <option value="500">{t('menu_settings.typography.font_weight_medium', 'Medium')}</option>
                  <option value="600">{t('menu_settings.typography.font_weight_semibold', 'Semi-bold')}</option>
                  <option value="700">{t('menu_settings.typography.font_weight_bold', 'Bold')}</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t('menu_settings.pricing_style.color', 'Price color')}</label>
                <div className="flex gap-2 items-center">
                  <input
                    type="color"
                    value={settings.priceColor ?? settings.secondaryColor ?? '#0A0C0B'}
                    onChange={(e) => handleColorChange('priceColor', e.target.value)}
                    className="h-9 w-14 rounded border border-gray-300 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={settings.priceColor ?? settings.secondaryColor ?? '#0A0C0B'}
                    onChange={(e) => handleColorChange('priceColor', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#7BD5B5] focus:border-transparent bg-white"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Live Preview Section - Compact and Integrated */}
     <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
  <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
      
      {/* Left section: Icon + Title */}
      <div className="flex items-center gap-3">
        <div className="p-2 bg-blue-50 rounded-lg">
          <Eye className="w-5 h-5 text-blue-600" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{t('menu_settings.preview.title')}</h3>
          <p className="text-sm text-gray-600">{t('menu_settings.preview.subtitle')}</p>
        </div>
      </div>

      {/* Right section: Buttons + Theme */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mt-3 sm:mt-0">
        {/* Theme indicator */}
        <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg border border-gray-200">
          <div
            className="w-2 h-2 rounded-full"
            style={{
              backgroundColor:
                publicMenuPage === 'default'
                  ? '#7BD5B5'
                  : publicMenuPage === 'light'
                  ? '#0A0C0B'
                  : '#ffffff',
            }}
          ></div>
          <span className="text-sm font-medium text-gray-700 capitalize">
            {publicMenuPage === 'default'
              ? 'Default Theme'
              : publicMenuPage === 'light'
              ? 'Light Theme'
              : 'Dark Theme'}
          </span>
        </div>

        {/* Public URL button */}
        <button
          type="button"
          onClick={() => setShowUrlSelector(true)}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 font-medium rounded-lg transition-colors"
        >
          <Globe className="w-4 h-4" />
         Cambia Template
        </button>

        {/* Show/Hide Preview */}
        <button
          type="button"
          onClick={() => setShowPreview(!showPreview)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg text-sm font-medium text-gray-700 transition-colors"
        >
          {showPreview ? (
            <>
              <EyeOff className="w-4 h-4" />
              {t('menu_settings.preview.hide')}
            </>
          ) : (
            <>
              <Eye className="w-4 h-4" />
              {t('menu_settings.preview.show')}
            </>
          )}
        </button>

        {/* Open in new tab */}
        <button
          type="button"
          onClick={handleOpenInNewTab}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
        >
          <ExternalLink className="w-4 h-4" />
          {t('menu_settings.preview.open')}
        </button>
      </div>
    </div>
  </div>

  {showPreview && (
    <div className="p-4">
      <div className="flex flex-col lg:flex-row gap-4">
        {/* Desktop Preview */}
        <div className="flex-1 border border-gray-300 rounded-lg overflow-hidden shadow-sm">
          <div className="px-4 py-2 bg-gray-100 border-b border-gray-300 flex items-center gap-2">
            <div className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full bg-red-400"></div>
              <div className="w-2.5 h-2.5 rounded-full bg-yellow-400"></div>
              <div className="w-2.5 h-2.5 rounded-full bg-green-400"></div>
            </div>
            <span className="text-xs text-gray-500 ml-2">Desktop</span>
          </div>
          <div className="relative bg-gray-50" style={{ height: '420px', overflow: 'hidden' }}>
            <iframe
              key={previewKey}
              src={getPreviewUrl()}
              className="w-full h-full border-0"
              title="Desktop Preview"
              sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              style={{
                transform: 'scale(0.55)',
                transformOrigin: '0 0',
                width: '181.8%',
                height: '181.8%',
              }}
            />
          </div>
        </div>

        {/* Mobile Preview */}
        <div className="flex-shrink-0 flex flex-col items-center">
          <div
            className="relative border-[3px] border-gray-800 rounded-[2rem] overflow-hidden shadow-lg bg-black"
            style={{ width: '220px', height: '440px' }}
          >
            {/* Phone notch */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-20 h-5 bg-gray-800 rounded-b-xl z-10"></div>
            {/* Phone status bar */}
            <div className="h-7 bg-gray-800 flex items-end justify-center pb-0.5">
              <span className="text-[8px] text-gray-400">Mobile</span>
            </div>
            {/* Phone content */}
            <div className="relative bg-white" style={{ height: 'calc(100% - 28px)', overflow: 'hidden' }}>
              <iframe
                key={`mobile-${previewKey}`}
                src={getPreviewUrl()}
                className="border-0"
                title="Mobile Preview"
                sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                style={{
                  transform: 'scale(0.28)',
                  transformOrigin: '0 0',
                  width: '357%',
                  height: '357%',
                }}
              />
            </div>
            {/* Phone home indicator */}
            <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 w-16 h-1 bg-gray-600 rounded-full"></div>
          </div>
        </div>
      </div>
    </div>
  )}
</div>


   {showUrlSelector && menuSlug && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
      <div className="flex items-center justify-between p-6 border-b border-gray-200">
        <div>
          <h3 className="text-xl font-bold text-gray-900">{t('menu_settings.public_url.title')}</h3>
          <p className="text-gray-600 mt-1 text-sm"> {t('menu_settings.subtitle')}</p>
        </div>
        <button
          type="button"
          onClick={() => setShowUrlSelector(false)}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
      
      <div className="flex-1 overflow-y-auto p-6">
       
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* DEFAULT THEME PREVIEW - Shows background image */}
          <div 
            className={`border-2 rounded-xl p-3 cursor-pointer transition-all hover:scale-[1.02] ${publicMenuPage === 'default' ? 'border-[#7BD5B5] ring-2 ring-[#7BD5B5]/20' : 'border-gray-200 hover:border-gray-300'}`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handlePublicUrlSelect('default');
              setShowUrlSelector(false);
            }}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full border-2 flex items-center justify-center" style={{ borderColor: '#7BD5B5' }}>
                  {publicMenuPage === 'default' && (
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#7BD5B5' }}></div>
                  )}
                </div>
                <h4 className="font-semibold text-gray-900 text-sm">{t('menu_settings.public_url.default_theme')}</h4>
              </div>
              {publicMenuPage === 'default' && (
                <span className="text-xs px-2 py-1 bg-[#7BD5B5] text-white rounded-full">{t('menu_settings.preview.selected')}</span>
              )}
            </div>
            
            {/* Screen Preview with Background Image */}
            <div className="relative mb-3">
              <div className="w-full h-32 rounded-lg border border-gray-300 overflow-hidden shadow-inner relative">
                {/* Background Image or Gradient Fallback */}
                {backgroundImagePreview ? (
                  <div 
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: `url(${backgroundImagePreview})` }}
                  >
                    {/* Overlay for better text visibility */}
                    <div className="absolute inset-0 bg-black/10 backdrop-blur-[1px]"></div>
                    
                    {/* Logo in top-left corner */}
                    {logoPreview && (
                      <div className="absolute top-2 left-2 w-6 h-6 rounded-full bg-white/90 backdrop-blur-sm p-0.5 flex items-center justify-center">
                        <img 
                          src={logoPreview} 
                          alt="Logo" 
                          className="w-full h-full object-contain"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      </div>
                    )}
                    
                    {/* Mock Screen Content - PRIMARY COLOR TEXT */}
                    <div className="absolute inset-0 p-2 pt-8">
                      {/* Title with primary color */}
                      <div 
                        className="h-3 rounded mb-2 w-3/4 backdrop-blur-sm ml-8"
                        style={{ backgroundColor: settings.primaryColor + 'CC' }} // CC = 80% opacity
                      ></div>
                      {/* Subtitle with primary color (lighter) */}
                      <div 
                        className="h-2 rounded mb-1 w-1/2 backdrop-blur-sm ml-8"
                        style={{ backgroundColor: settings.primaryColor + '99' }} // 99 = 60% opacity
                      ></div>
                      <div 
                        className="h-2 rounded mb-1 w-2/3 backdrop-blur-sm ml-8"
                        style={{ backgroundColor: settings.primaryColor + '99' }}
                      ></div>
                      {/* Menu items with primary color variations */}
                      <div className="mt-4">
                        <div 
                          className="h-5 rounded-lg mb-1 backdrop-blur-sm"
                          style={{ backgroundColor: settings.primaryColor + 'E6' }} // E6 = 90% opacity
                        ></div>
                        <div 
                          className="h-5 rounded-lg mb-1 backdrop-blur-sm"
                          style={{ backgroundColor: settings.primaryColor + 'B3' }} // B3 = 70% opacity
                        ></div>
                        <div 
                          className="h-5 rounded-lg backdrop-blur-sm"
                          style={{ backgroundColor: settings.primaryColor + '80' }} // 80 = 50% opacity
                        ></div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-br from-green-50 to-emerald-100">
                    {/* Logo in top-left corner */}
                    {logoPreview && (
                      <div className="absolute top-2 left-2 w-6 h-6 rounded-full bg-white/90 p-0.5 flex items-center justify-center">
                        <img 
                          src={logoPreview} 
                          alt="Logo" 
                          className="w-full h-full object-contain"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      </div>
                    )}
                    
                    {/* Mock Screen Content - PRIMARY COLOR TEXT */}
                    <div className="absolute inset-0 p-2 pt-8">
                      {/* Title with primary color */}
                      <div 
                        className="h-3 rounded mb-2 w-3/4 ml-8"
                        style={{ backgroundColor: settings.primaryColor + 'CC' }}
                      ></div>
                      {/* Subtitle with primary color (lighter) */}
                      <div 
                        className="h-2 rounded mb-1 w-1/2 ml-8"
                        style={{ backgroundColor: settings.primaryColor + '99' }}
                      ></div>
                      <div 
                        className="h-2 rounded mb-1 w-2/3 ml-8"
                        style={{ backgroundColor: settings.primaryColor + '99' }}
                      ></div>
                      {/* Menu items with primary color variations */}
                      <div className="mt-4">
                        <div 
                          className="h-5 rounded-lg mb-1"
                          style={{ backgroundColor: settings.primaryColor + 'E6' }}
                        ></div>
                        <div 
                          className="h-5 rounded-lg mb-1"
                          style={{ backgroundColor: settings.primaryColor + 'B3' }}
                        ></div>
                        <div 
                          className="h-5 rounded-lg"
                          style={{ backgroundColor: settings.primaryColor + '80' }}
                        ></div>
                      </div>
                    </div>
                  </div>
                )}
                
                
              </div>
              
            
            </div>
            
           
          </div>
          
          {/* LIGHT THEME PREVIEW */}
          <div 
            className={`border-2 rounded-xl p-3 cursor-pointer transition-all hover:scale-[1.02] ${publicMenuPage === 'light' ? 'border-[#7BD5B5] ring-2 ring-[#7BD5B5]/20' : 'border-gray-200 hover:border-gray-300'}`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handlePublicUrlSelect('light');
              setShowUrlSelector(false);
            }}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full border-2 flex items-center justify-center" style={{ borderColor: '#7BD5B5' }}>
                  {publicMenuPage === 'light' && (
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#7BD5B5' }}></div>
                  )}
                </div>
                <h4 className="font-semibold text-gray-900 text-sm">{t('menu_settings.public_url.light_theme')}</h4>
              </div>
              {publicMenuPage === 'light' && (
                <span className="text-xs px-2 py-1 bg-[#7BD5B5] text-white rounded-full">{t('menu_settings.preview.selected')}</span>
              )}
            </div>
            
            {/* Screen Preview */}
            <div className="relative mb-3">
              <div className="w-full h-32 bg-white rounded-lg border border-gray-300 overflow-hidden shadow-inner">
                {/* Logo in top-left corner */}
                {logoPreview && (
                  <div className="absolute top-2 left-2 w-6 h-6 rounded-full bg-gray-100 p-0.5 flex items-center justify-center z-10">
                    <img 
                      src={logoPreview} 
                      alt="Logo" 
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                )}
                
                {/* Mock Screen Content - BLACK TEXT */}
                <div className="absolute inset-0 p-2 pt-8">
                  {/* Title with black */}
                  <div className="h-3 bg-gray-800 rounded mb-2 w-3/4 ml-8"></div>
                  {/* Subtitle with dark gray */}
                  <div className="h-2 bg-gray-600 rounded mb-1 w-1/2 ml-8"></div>
                  <div className="h-2 bg-gray-600 rounded mb-1 w-2/3 ml-8"></div>
                  {/* Menu items with black variations */}
                  <div className="mt-4">
                    <div className="h-5 bg-gray-900 rounded-lg mb-1"></div>
                    <div className="h-5 bg-gray-800 rounded-lg mb-1"></div>
                    <div className="h-5 bg-gray-700 rounded-lg"></div>
                  </div>
                </div>
                
             
              </div>
              
             
            </div>
            
            <p className="text-xs text-gray-500 text-center">{t('menu_settings.preview.clean_white_background')}</p>
          </div>
          
          {/* DARK THEME PREVIEW */}
          <div 
            className={`border-2 rounded-xl p-3 cursor-pointer transition-all hover:scale-[1.02] ${publicMenuPage === 'dark' ? 'border-[#7BD5B5] ring-2 ring-[#7BD5B5]/20 bg-gray-800' : 'border-gray-200 hover:border-gray-300'}`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handlePublicUrlSelect('dark');
              setShowUrlSelector(false);
            }}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full border-2 flex items-center justify-center" style={{ borderColor: '#7BD5B5' }}>
                  {publicMenuPage === 'dark' && (
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#7BD5B5' }}></div>
                  )}
                </div>
                <h4 className="font-semibold text-white text-sm">{t('menu_settings.public_url.dark_theme')}</h4>
              </div>
              {publicMenuPage === 'dark' && (
                <span className="text-xs px-2 py-1 bg-[#7BD5B5] text-white rounded-full">{t('menu_settings.preview.selected')}</span>
              )}
            </div>
            
            {/* Screen Preview */}
            <div className="relative mb-3">
              <div className="w-full h-32 bg-gray-900 rounded-lg border border-gray-700 overflow-hidden shadow-inner">
                {/* Logo in top-left corner */}
                {logoPreview && (
                  <div className="absolute top-2 left-2 w-6 h-6 rounded-full bg-gray-800 p-0.5 flex items-center justify-center z-10">
                    <img 
                      src={logoPreview} 
                      alt="Logo" 
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                )}
                
                {/* Mock Screen Content - WHITE TEXT */}
                <div className="absolute inset-0 p-2 pt-8">
                  {/* Title with white */}
                  <div className="h-3 bg-gray-100 rounded mb-2 w-3/4 ml-8"></div>
                  {/* Subtitle with light gray */}
                  <div className="h-2 bg-gray-300 rounded mb-1 w-1/2 ml-8"></div>
                  <div className="h-2 bg-gray-300 rounded mb-1 w-2/3 ml-8"></div>
                  {/* Menu items with white variations */}
                  <div className="mt-4">
                    <div className="h-5 bg-gray-50 rounded-lg mb-1"></div>
                    <div className="h-5 bg-gray-200 rounded-lg mb-1"></div>
                   
                  </div>
                </div>
               
              </div>
              
            
            </div>
            
          </div>
   {/* Lightcomplex THEME PREVIEW */}
 <div 
            className={`border-2 rounded-xl p-3 cursor-pointer transition-all hover:scale-[1.02] ${publicMenuPage === 'lightComplex' ? 'border-[#7BD5B5] ring-2 ring-[#7BD5B5]/20' : 'border-gray-200 hover:border-gray-300'}`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handlePublicUrlSelect('lightComplex');
              setShowUrlSelector(false);
            }}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full border-2 flex items-center justify-center" style={{ borderColor: '#7BD5B5' }}>
                  {publicMenuPage === 'lightComplex' && (
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#7BD5B5' }}></div>
                  )}
                </div>
                <h4 className="font-semibold text-gray-900 text-sm">{t('menu_settings.public_url.light_theme')} complex</h4>
              </div>
              {publicMenuPage === 'lightComplex' && (
                <span className="text-xs px-2 py-1 bg-[#7BD5B5] text-white rounded-full">{t('menu_settings.preview.selected')}</span>
              )}
            </div>
            
            {/* Screen Preview */}
            <div className="relative mb-3">
              <div className="w-full h-32 bg-white rounded-lg border border-gray-300 overflow-hidden shadow-inner">
                {/* Logo in top-left corner */}
                {logoPreview && (
                  <div className="absolute top-2 left-2 w-6 h-6 rounded-full bg-gray-100 p-0.5 flex items-center justify-center z-10">
                    <img 
                      src={logoPreview} 
                      alt="Logo" 
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                )}
                
                {/* Mock Screen Content - BLACK TEXT */}
                <div className="absolute inset-0 p-2 pt-8">
                  {/* Title with black */}
                  <div className="h-3 bg-gray-800 rounded mb-2 w-3/4 ml-8"></div>
                  {/* Subtitle with dark gray */}
                  <div className="h-2 bg-gray-600 rounded mb-1 w-1/2 ml-8"></div>
                  <div className="h-2 bg-gray-600 rounded mb-1 w-2/3 ml-8"></div>
                  {/* Menu items with black variations */}
                  <div className="mt-4">
                    <div className="h-5 bg-gray-900 rounded-lg mb-1"></div>
                    <div className="h-5 bg-gray-800 rounded-lg mb-1"></div>
                    <div className="h-5 bg-gray-700 rounded-lg"></div>
                  </div>
                </div>
                
             
              </div>
              
             
            </div>
            
            <p className="text-xs text-gray-500 text-center">{t('menu_settings.preview.clean_white_background')}</p>
          </div>
  {/* darkComplex THEME PREVIEW */}
          <div 
            className={`border-2 rounded-xl p-3 cursor-pointer transition-all hover:scale-[1.02] ${publicMenuPage === 'darkComplex' ? 'border-[#7BD5B5] ring-2 ring-[#7BD5B5]/20 bg-gray-800' : 'border-gray-200 hover:border-gray-300'}`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handlePublicUrlSelect('darkComplex');
              setShowUrlSelector(false);
            }}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full border-2 flex items-center justify-center" style={{ borderColor: '#7BD5B5' }}>
                  {publicMenuPage === 'darkComplex' && (
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#7BD5B5' }}></div>
                  )}
                </div>
                <h4 className="font-semibold text-white text-sm">{t('menu_settings.public_url.dark_theme')}</h4>
              </div>
              {publicMenuPage === 'darkComplex' && (
                <span className="text-xs px-2 py-1 bg-[#7BD5B5] text-white rounded-full">{t('menu_settings.preview.selected')}</span>
              )}
            </div>
            
            {/* Screen Preview */}
            <div className="relative mb-3">
              <div className="w-full h-32 bg-gray-900 rounded-lg border border-gray-700 overflow-hidden shadow-inner">
                {/* Logo in top-left corner */}
                {logoPreview && (
                  <div className="absolute top-2 left-2 w-6 h-6 rounded-full bg-gray-800 p-0.5 flex items-center justify-center z-10">
                    <img 
                      src={logoPreview} 
                      alt="Logo" 
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                )}
                
                {/* Mock Screen Content - WHITE TEXT */}
                <div className="absolute inset-0 p-2 pt-8">
                  {/* Title with white */}
                  <div className="h-3 bg-gray-100 rounded mb-2 w-3/4 ml-8"></div>
                  {/* Subtitle with light gray */}
                  <div className="h-2 bg-gray-300 rounded mb-1 w-1/2 ml-8"></div>
                  <div className="h-2 bg-gray-300 rounded mb-1 w-2/3 ml-8"></div>
                  {/* Menu items with white variations */}
                  <div className="mt-4">
                    <div className="h-5 bg-gray-50 rounded-lg mb-1"></div>
                    <div className="h-5 bg-gray-200 rounded-lg mb-1"></div>
                   
                  </div>
                </div>
               
              </div>
              
            
            </div>
            
          </div>


        </div>
       
      </div>
      
      <div className="p-4 border-t border-gray-200 bg-gray-50">
        <div className="flex justify-between items-center">
          <p className="text-sm text-gray-600">
            Selected: <span className="font-medium capitalize">{publicMenuPage === 'default' ? 'Default' : publicMenuPage} theme</span>
          </p>
          <button
            type="button"
            onClick={() => setShowUrlSelector(false)}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors text-sm font-medium"
          >
            {t('menu_settings.preview.close')}
          </button>
        </div>
      </div>
    </div>
  </div>
)}
    </div>
  );
};

export default MenuSettings;